"use client";

import { useSearchParams } from "next/navigation";
import { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Button from "~/components/buttons/Button";
import Flex from "~/components/flex/Flex";
import { Icon, IconName } from "~/components/icons/Icon";
import { TextInput } from "~/components/inputs/TextInput";
import { NumberInput } from "~/components/inputs/NumberInput";
import Dropdown from "~/components/inputs/Dropdown";
import DatePickerButton from "~/components/datePicker/DatePickerButton";
import Modal from "~/components/modal/Modal";
import Text from "~/components/texts/Text";
import { EMAIL_PROCUREMENT, SEARCH_PARAM_CREATE_SINGLE_OFFER_MODAL } from "~/constants";
import { OFFER_CREATED } from "~/constants/queryParams";
import { INCOTERM_OPTIONS } from "~/constants/pqIngestionConstants";
import { ENDPOINT } from "~/constants/endpoints";
import {
  ButtonTypesEnum,
  UnitTypeEnum,
  AvailabilityTypeEnum,
  PriceUnitEnum,
  ProductCategory,
} from "~/types-and-enums/generalTypes";
import { ProductSearchResult } from "~/types-and-enums/pqIngestionTypes";
import { updateSearchParams } from "~/utils/clientUtils";
import { colors, spacing } from "~/utils/tailwindUtils";
import * as apiClient from "~/utils/apiClient";
import { createSingleOffering } from "../actions";
import { CreateSingleOfferingPayload } from "~/types-and-enums/pqIngestionTypes";
import IncotermLocationSelect from "~/components/incoterm-location-select/IncotermLocationSelect";
import { SelectedIncotermLocation } from "~/types-and-enums/incotermLocationTypes";
import { formatIncotermLocationShort, toIncotermLocationPayloadFields } from "~/utils/incotermLocationUtils";
import { formatDateToYYYYMMDD } from "~/utils/dateUtils";
import ProductImage from "~/components/product-image/ProductImage";
import Switch from "~/components/switch/NewSwitch";
import VerticalLine from "~/components/vertical-line/VerticalLine";
import HorizontalLine from "~/components/horizontalLine/HorizontalLine";
import CollapsibleSection from "~/components/collapsible-section/CollapsibleSection";
import { useI18n } from "~/locales/client";
import AndercoreLogoLoadingGif from "~/components/loader/AndercoreLogoLoadingGif";
import { usePostHog } from "~/hooks/usePostHog";
import { twMerge } from "tailwind-merge";

/**
 * Create Single Offer Modal Component
 *
 * Allows users to create a single product offering manually
 */
const CreateSingleOfferModal = () => {
  const searchParams = useSearchParams();
  const translate = useI18n();
  const { captureEvent } = usePostHog();
  const [selectedProduct, setSelectedProduct] = useState<ProductSearchResult | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productSearchResults, setProductSearchResults] = useState<ProductSearchResult[]>([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const productInputRef = useRef<HTMLInputElement | null>(null);
  const [productDropdownPosition, setProductDropdownPosition] = useState<{ top: number; left: number } | null>(null);

  // Form fields
  const [availableQuantity, setAvailableQuantity] = useState("");
  const [availableQuantityUnit, setAvailableQuantityUnit] = useState<UnitTypeEnum>(UnitTypeEnum.pieces);
  const [availabilityType, setAvailabilityType] = useState<AvailabilityTypeEnum>(AvailabilityTypeEnum.IN_STOCK_NOW);
  const [availableFromDate, setAvailableFromDate] = useState<Date | null>(new Date());
  const [leadTime, setLeadTime] = useState("");
  const [moq, setMoq] = useState("");
  const [moqUnit, setMoqUnit] = useState<UnitTypeEnum>(UnitTypeEnum.pieces);
  const [price, setPrice] = useState("");
  const [priceUnit, setPriceUnit] = useState<PriceUnitEnum>(PriceUnitEnum.pieces);
  const [incoterm, setIncoterm] = useState("");
  const [incotermLocation, setIncotermLocation] = useState<SelectedIncotermLocation | null>(null);
  const [includedLogisticCost, setIncludedLogisticCost] = useState(false);
  const [validUntilDate, setValidUntilDate] = useState<Date | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createOfferShakeTrigger, setCreateOfferShakeTrigger] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    product?: boolean;
    availableQuantity?: boolean;
    availableFromDate?: boolean;
    leadTime?: boolean;
    moq?: boolean;
    moqExceedsQuantity?: boolean;
    price?: boolean;
    validUntilDate?: boolean;
    incoterm?: boolean;
    incotermLocation?: boolean;
  }>({});

  const shouldOpen = searchParams.get(SEARCH_PARAM_CREATE_SINGLE_OFFER_MODAL) === "true";

  const getRequiredFieldError = (label: string) =>
    `${label} ${translate("single-product-offering.is-required")}`;

  const handleClose = (): void => {
    if (isSubmitting) return;

    const params = new URLSearchParams(searchParams);
    params.delete(SEARCH_PARAM_CREATE_SINGLE_OFFER_MODAL);
    updateSearchParams(params);
    // Reset state
    setSelectedProduct(null);
    setProductSearchTerm("");
    setProductSearchResults([]);
    setShowProductDropdown(false);
    setError(null);
    setFieldErrors({});
    // Reset form fields
    setAvailableQuantity("");
    setAvailableQuantityUnit(UnitTypeEnum.pieces);
    setAvailabilityType(AvailabilityTypeEnum.IN_STOCK_NOW);
    setAvailableFromDate(new Date());
    setLeadTime("");
    setMoq("");
    setMoqUnit(UnitTypeEnum.pieces);
    setPrice("");
    setPriceUnit(PriceUnitEnum.pieces);
    setIncoterm("");
    setIncotermLocation(null);
    setIncludedLogisticCost(false);
    setValidUntilDate(null);
  };

  // Search products with debouncing
  const searchProducts = useCallback(async (searchText: string) => {
    // Clear existing timeout
    if (productSearchTimeoutRef.current) {
      clearTimeout(productSearchTimeoutRef.current);
    }

    // If search text is too short, clear results and hide dropdown
    if (!searchText || searchText.trim().length < 2) {
      setProductSearchResults([]);
      setShowProductDropdown(false);
      setProductSearchLoading(false);
      return;
    }

    // Debounce the search
    productSearchTimeoutRef.current = setTimeout(async () => {
      setProductSearchLoading(true);

      try {
        const response = await apiClient.get<ProductSearchResult[]>(ENDPOINT.products.search, {
          params: { name: searchText.trim() },
        });

        const results = response.data ?? [];
        setProductSearchResults(results);
        setShowProductDropdown(true);
      } catch (error) {
        console.error("Error searching products:", error);
        setProductSearchResults([]);
        setShowProductDropdown(true);
      } finally {
        setProductSearchLoading(false);
      }
    }, 500); // 500ms debounce
  }, []);

  // Handle product selection — reset all form values when product changes
  const handleProductSelect = useCallback((product: ProductSearchResult, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setSelectedProduct(product);
    setProductSearchTerm(product.name);
    setShowProductDropdown(false);
    setError(null);
    setFieldErrors({});
    setAvailableQuantity("");
    setAvailableQuantityUnit(UnitTypeEnum.pieces);
    setAvailabilityType(AvailabilityTypeEnum.IN_STOCK_NOW);
    setAvailableFromDate(new Date());
    setLeadTime("");
    setMoq("");
    setMoqUnit(UnitTypeEnum.pieces);
    setPrice("");
    setPriceUnit(PriceUnitEnum.pieces);
    setIncoterm("");
    setIncotermLocation(null);
    setIncludedLogisticCost(false);
    setValidUntilDate(null);
  }, []);

  // Calculate product dropdown position
  useLayoutEffect(() => {
    if (!showProductDropdown || !productInputRef.current) {
      setProductDropdownPosition(null);
      return;
    }

    const updatePosition = () => {
      if (productInputRef.current) {
        const rect = productInputRef.current.getBoundingClientRect();
        setProductDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
    };

    updatePosition();

    // Update positions on scroll and resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [showProductDropdown, productSearchResults]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking on the dropdown or its children
      if (target.closest('[data-product-dropdown]')) {
        return;
      }
      // Don't close if clicking on the input
      if (productInputRef.current && productInputRef.current.contains(target)) {
        return;
      }
      // Close dropdown if clicking outside
      setShowProductDropdown(false);
    };

    if (showProductDropdown) {
      // Use a slight delay to allow click events on dropdown items to process first
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showProductDropdown]);

  // When product is not PV Modules (ProductCategory.Modules), ensure price unit is pieces (Wattpeak option is hidden)
  useEffect(() => {
    if (selectedProduct && selectedProduct.category !== ProductCategory.Modules && priceUnit === PriceUnitEnum.wattpeak) {
      setPriceUnit(PriceUnitEnum.pieces);
    }
  }, [selectedProduct, priceUnit]);

  const handleSubmit = async () => {
    const nextFieldErrors = {
      product: !selectedProduct,
      availableQuantity: !availableQuantity,
      availableFromDate: availabilityType !== AvailabilityTypeEnum.ALWAYS_IN_STOCK && !availableFromDate,
      leadTime: false,
      moq: !moq,
      price: !price,
      validUntilDate: !validUntilDate,
      incoterm: !incoterm,
      // A new offer must use a location linked to an incoterm location record
      incotermLocation: !incotermLocation?.id,
    };

    setFieldErrors(nextFieldErrors);

    if (Object.values(nextFieldErrors).some(Boolean)) {
      // Missing field errors are shown inline under each input.
      setError(null);
      setCreateOfferShakeTrigger((prev) => prev + 1);
      return;
    }

    // Store validated values for use in payload
    const validatedValidUntilDate = validUntilDate!;
    const validatedProduct = selectedProduct!;
    const validatedIncotermLocation = incotermLocation!;

    // Validate quantities
    const quantityNum = parseInt(availableQuantity, 10);
    const moqNum = parseInt(moq, 10);
    if (isNaN(quantityNum) || isNaN(moqNum) || moqNum < 0 || quantityNum < 0) {
      setError(translate("single-product-offering.error.quantities-invalid"));
      return;
    }

    // Calculate maximum_available_quantity based on unit
    let maximumAvailableQuantity: number;
    if (availableQuantityUnit === UnitTypeEnum.pallets) {
      if (!validatedProduct.units_per_pallet || validatedProduct.units_per_pallet <= 0) {
        setError(translate("single-product-offering.error.units-per-pallet-missing"));
        return;
      }
      maximumAvailableQuantity = validatedProduct.units_per_pallet * quantityNum;
    } else if (availableQuantityUnit === UnitTypeEnum.containers) {
      if (!validatedProduct.units_per_container || validatedProduct.units_per_container <= 0) {
        setError(translate("single-product-offering.error.units-per-container-missing"));
        return;
      }
      maximumAvailableQuantity = validatedProduct.units_per_container * quantityNum;
    } else {
      // pieces
      maximumAvailableQuantity = quantityNum;
    }

    // Calculate minimum_order_quantity based on selected MOQ unit (always in pieces)
    let minimumOrderQuantity: number;
    if (moqUnit === UnitTypeEnum.pallets) {
      if (!validatedProduct.units_per_pallet || validatedProduct.units_per_pallet <= 0) {
        setError(translate("single-product-offering.error.units-per-pallet-missing"));
        return;
      }
      minimumOrderQuantity = moqNum * validatedProduct.units_per_pallet;
    } else if (moqUnit === UnitTypeEnum.containers) {
      if (!validatedProduct.units_per_container || validatedProduct.units_per_container <= 0) {
        setError(translate("single-product-offering.error.units-per-container-missing"));
        return;
      }
      minimumOrderQuantity = moqNum * validatedProduct.units_per_container;
    } else {
      minimumOrderQuantity = moqNum;
    }

    if (minimumOrderQuantity > maximumAvailableQuantity) {
      setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: true }));
      return;
    }

    // Validate price
    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum < 0) {
      setError(translate("single-product-offering.error.price-invalid"));
      return;
    }



    setError(null);
    setIsSubmitting(true);

    try {
      // Determine price values based on price unit
      let unitPurchasePrice: number | undefined = undefined;
      let pricePerWatt: number | undefined = undefined;

      if (priceUnit === PriceUnitEnum.wattpeak) {
        // When price per wattpeak is selected, assign to price_per_watt
        pricePerWatt = parseFloat(price);
      } else {
        // When price per pieces is selected, assign to unit_purchase_price
        unitPurchasePrice = parseFloat(price);
      }

      // Build payload
      const payload: CreateSingleOfferingPayload = {
        minimum_order_quantity: minimumOrderQuantity,
        pq_type: availabilityType,
        maximum_available_quantity: maximumAvailableQuantity,
        ...(unitPurchasePrice !== undefined && { unit_purchase_price: unitPurchasePrice }),
        ...(pricePerWatt !== undefined && { price_per_watt: pricePerWatt }),
        valid_until: formatDateToYYYYMMDD(validatedValidUntilDate),
        // earliest_availability_date: only for UPCOMING_STOCK and IN_STOCK_NOW
        ...(availabilityType !== AvailabilityTypeEnum.ALWAYS_IN_STOCK && availableFromDate && {
          earliest_availability_date: formatDateToYYYYMMDD(availableFromDate),
        }),
        // lead_time: always sent, defaults to 0 when not provided
        lead_time: leadTime.trim() !== "" && !isNaN(parseInt(leadTime, 10)) && parseInt(leadTime, 10) >= 0
          ? parseInt(leadTime, 10)
          : 0,
        sf_product: {
          name: validatedProduct.name,
          id: validatedProduct.id,
          is_active: true,
        },
        incoterm,
        ...toIncotermLocationPayloadFields(validatedIncotermLocation),
        includes_logistics_cost: includedLogisticCost,
      };

      const result = await createSingleOffering(payload);

      if (result.success) {
        captureEvent("stock_offer_created", {
          product_name: validatedProduct.name,
          product_id: validatedProduct.id,
          availability_type: availabilityType,
          incoterm,
          incoterm_location: formatIncotermLocationShort(validatedIncotermLocation),
          incoterm_location_id: validatedIncotermLocation.id,
          price: parseFloat(price) || null,
          price_unit: priceUnit,
          available_quantity: parseInt(availableQuantity, 10) || null,
          moq,
          valid_until: validUntilDate ? validUntilDate.toISOString().split("T")[0] : null,
          lead_time: leadTime || null,
          includes_logistics_cost: includedLogisticCost,
        });
        // Close modal, show success toast, and reload page
        handleClose();
        const params = new URLSearchParams(window.location.search);
        params.set(OFFER_CREATED, "true");
        updateSearchParams(params);
        window.location.reload();
      } else {
        setError(result.error || translate("single-product-offering.error.create-failed"));
      }
    } catch (err) {
      setError(translate("single-product-offering.error.create-failed"));
      console.error("Error creating offering:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Memoize product details section to prevent image flickering on state changes
  const productDetailsSection = useMemo(() => {
    if (!selectedProduct) return null;
    return (
      <Flex className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex-col gap-4">
        <Flex className="flex-col gap-2">
          <Flex className="items-center gap-4">
            <ProductImage
              imageUrl={selectedProduct.image_url}
              width={80}
              height={80}
              className="w-20 h-20 object-contain rounded border border-gray-200 bg-white"
            />
            <Flex className="flex-col gap-1 flex-1 justify-center">
              <Text className="text-base font-bold text-gray-900">{selectedProduct.name}</Text>
              {(selectedProduct.brand || selectedProduct.model) && (
                <Flex className="items-center gap-2">
                  {selectedProduct.brand && (
                    <Text className="text-sm text-gray-600">{selectedProduct.brand}</Text>
                  )}
                  {selectedProduct.brand && selectedProduct.model && (
                    <Text className="text-sm text-gray-400">•</Text>
                  )}
                  {selectedProduct.model && (
                    <Text className="text-sm text-gray-600">{selectedProduct.model}</Text>
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  }, [selectedProduct]);

  if (!shouldOpen) return null;

  return (
    <Modal
      isOpen={shouldOpen}
      closeModal={handleClose}
      outsideClick={!isSubmitting}
      size="lg"
      modalContainerClassname="w-full max-w-[98vw] sm:max-w-[640px] md:max-w-[700px] lg:max-w-[800px] max-h-[90vh] flex flex-col"
      classname="max-w-full! flex-1 flex flex-col overflow-hidden md:pb-6"
      childrenDivWrapperClassName="flex-1 flex flex-col overflow-hidden"
    >
      <div className="relative w-full h-full flex flex-col min-h-0">
        <Flex
          className={twMerge(
            "flex-col gap-8 w-full mx-auto flex-1 overflow-y-auto min-h-0",
            isSubmitting && "opacity-70 pointer-events-none"
          )}
        >
          {/* Title */}
          <Flex className="flex-col gap-2 shrink-0">
            <Flex className="items-center gap-3">
              {selectedProduct && (
                <button
                  type="button"
                  onClick={() => setSelectedProduct(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg border-2 border-red-500 transition-colors flex items-center justify-center"
                  aria-label="Back to product search"
                >
                  <Icon name={IconName.ArrowLeft} size={spacing[5]} color={colors.red[500]} />
                </button>
              )}
              <Flex className="flex-col gap-1 flex-1">
                <Text className="text-2xl md:text-3xl font-bold text-gray-900">
                  {translate("single-product-offering.title")}
                </Text>
                <Text className="text-sm text-gray-500">{translate("single-product-offering.subtitle")}</Text>
              </Flex>
            </Flex>
          </Flex>

          {/* Error Message */}
          {error && (
            <Flex className="bg-red-50 border border-red-200 rounded-lg p-3 shrink-0">
              <Text className="text-sm text-red-700 wrap-break-word">{error}</Text>
            </Flex>
          )}

          {/* Step 1: Search and Select Product */}
          {!selectedProduct ? (
            <Flex className="flex-col gap-8 shrink-0">
              {/* Add new offer for an existing product */}
              <Flex className="flex-col gap-2 py-3 px-2 md:py-6 md:px-4 bg-gray-50 rounded-lg">
                <Text className="text-base font-semibold text-gray-900">
                  {translate("single-product-offering.add-new-offer")}
                </Text>
                <div className="relative">
                  <Flex className="items-center gap-2">
                    <div
                      ref={(el) => {
                        if (el) {
                          const input = el.querySelector('input') as HTMLInputElement;
                          if (input) {
                            productInputRef.current = input;
                          }
                        }
                      }}
                      className="relative flex-1"
                    >
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-gray-400"
                        >
                          <circle cx="11" cy="11" r="8"></circle>
                          <path d="m21 21-4.35-4.35"></path>
                        </svg>
                      </div>
                      <TextInput
                        name="product-search"
                        value={productSearchTerm}
                        onChange={(e) => {
                          const val = e.target.value;
                          setProductSearchTerm(val);
                          if (val.trim().length >= 2) {
                            searchProducts(val);
                          } else {
                            setProductSearchResults([]);
                            setShowProductDropdown(false);
                          }
                        }}
                        placeholder={translate("single-product-offering.search-placeholder")}
                        classNames="w-full pl-10"
                        autoComplete="off"
                      />
                      {productSearchLoading && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                          <Icon name={IconName.Loader} size={spacing[4]} color={colors.gray[500]} className="animate-spin" />
                        </div>
                      )}
                    </div>
                    <Button
                      buttonStyle={ButtonTypesEnum.primary}
                      text={translate("single-product-offering.search")}
                      onClick={() => {
                        if (productSearchTerm.trim().length >= 2) {
                          searchProducts(productSearchTerm);
                        }
                      }}
                      disabled={productSearchTerm.trim().length < 2}
                      buttonClassName="w-fit px-6 py-2.5 rounded-lg"
                    />
                  </Flex>
                </div>
              </Flex>

              {/* Missing product on the list */}
              <Flex className="flex-col">
                <Text className="text-base font-semibold text-gray-900">{translate("single-product-offering.missing-product")}</Text>
                <Flex className="items-center justify-between gap-4">
                  <Text className="text-sm text-gray-600 flex-1">
                    {translate("single-product-offering.contact-message")}
                  </Text>
                  <Button
                    buttonStyle={ButtonTypesEnum.default}
                    text={translate("single-product-offering.contact-us")}
                    onClick={() => {
                      window.location.href = `mailto:${EMAIL_PROCUREMENT}?subject=${encodeURIComponent(translate("single-product-offering.could-not-find-product"))}`;
                    }}
                    buttonClassName="w-fit px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 shrink-0"
                  />
                </Flex>
              </Flex>
            </Flex>
          ) : (
            /* Step 2: Form Fields */
            <Flex className="flex-col gap-6 shrink-0">
              {/* Product Details */}
              {productDetailsSection}

              {/* Offer Details */}
              <Flex className="flex-col gap-6 border border-gray-200 rounded-lg p-4">
                <CollapsibleSection
                  title={translate("single-product-offering.offer-details")}
                  defaultExpanded={true}
                  contentClassName="gap-4 lg:gap-6"
                >
                  {/* Row 1: Available Quantity */}
                  <Flex className="flex-col gap-4 pt-4">
                    <Flex className="flex-col gap-2">
                      <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.available-quantity")} *</Text>
                      <Flex className="items-start gap-2">
                        <Dropdown
                          name="quantity-unit"
                          options={[
                            { value: UnitTypeEnum.pieces, label: translate("single-product-offering.pieces") },
                            { value: UnitTypeEnum.pallets, label: translate("single-product-offering.pallets") },
                            { value: UnitTypeEnum.containers, label: translate("single-product-offering.containers") },
                          ]}
                          value={availableQuantityUnit}
                          onChange={(value) => setAvailableQuantityUnit(value as UnitTypeEnum)}
                          classNames="flex-1 min-w-0"
                          useRelativePositioning={true}
                        />
                        <NumberInput
                          name="available-quantity"
                          value={availableQuantity}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || (!isNaN(Number(val)) && Number(val) >= 0)) {
                              setAvailableQuantity(val);
                              setFieldErrors((prev) => ({ ...prev, availableQuantity: false, moqExceedsQuantity: false }));
                            }
                          }}
                          placeholder="Quantity"
                          classNames={twMerge("flex-1 min-w-0", fieldErrors.availableQuantity && "border-red-500")}
                        />
                        <Flex className="flex-col gap-2 mt-[-24px] flex-1 min-w-0">
                          <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.pieces")}</Text>
                          <TextInput
                            name="available-quantity-pieces"
                            value={
                              availableQuantity && !isNaN(Number(availableQuantity)) && Number(availableQuantity) > 0
                                ? availableQuantityUnit === UnitTypeEnum.pieces
                                  ? availableQuantity
                                  : availableQuantityUnit === UnitTypeEnum.pallets && selectedProduct?.units_per_pallet
                                    ? (Number(availableQuantity) * selectedProduct.units_per_pallet).toString()
                                    : availableQuantityUnit === UnitTypeEnum.containers && selectedProduct?.units_per_container
                                      ? (Number(availableQuantity) * selectedProduct.units_per_container).toString()
                                      : ""
                                : ""
                            }
                            isDisabled={true}
                            classNames="w-full"
                          />
                        </Flex>
                      </Flex>
                      {fieldErrors.availableQuantity && (
                        <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                          {getRequiredFieldError(translate("single-product-offering.available-quantity"))}
                        </Text>
                      )}
                    </Flex>

                    {/* Row 2: Available Time Period */}
                    <Flex className="flex-col gap-2">
                      <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.available-time-period")} *</Text>
                      <Flex className="items-center gap-2">
                        <Dropdown
                          name="availability-type"
                          options={[
                            { value: AvailabilityTypeEnum.ALWAYS_IN_STOCK, label: translate("single-product-offering.always-in-stock") },
                            { value: AvailabilityTypeEnum.UPCOMING_STOCK, label: translate("single-product-offering.upcoming-stock") },
                            { value: AvailabilityTypeEnum.IN_STOCK_NOW, label: translate("single-product-offering.in-stock-now") },
                          ]}
                          value={availabilityType}
                          onChange={(value) => {
                            const newType = value as AvailabilityTypeEnum;
                            setAvailabilityType(newType);
                            if (newType === AvailabilityTypeEnum.IN_STOCK_NOW) {
                              setAvailableFromDate(new Date());
                              setFieldErrors((prev) => ({ ...prev, availableFromDate: false }));
                            } else if (newType === AvailabilityTypeEnum.UPCOMING_STOCK) {
                              setAvailableFromDate(null);
                            } else {
                              setAvailableFromDate(null);
                              setFieldErrors((prev) => ({ ...prev, availableFromDate: false }));
                            }
                          }}
                          classNames="flex-1 min-w-0"
                          useRelativePositioning={true}
                        />
                        {/* Date picker — only for date-based types */}
                        {availabilityType !== AvailabilityTypeEnum.ALWAYS_IN_STOCK && (
                          <DatePickerButton
                            value={availableFromDate || undefined}
                            onChange={(date) => {
                              setAvailableFromDate(date);
                              setFieldErrors((prev) => ({ ...prev, availableFromDate: false }));
                            }}
                            placeholder="dd.mm.yyyy"
                            classNames={twMerge("flex-1 min-w-0", fieldErrors.availableFromDate && "border-red-500")}
                            uniqueQueryId="availability-date"
                            isDisabled={availabilityType === AvailabilityTypeEnum.IN_STOCK_NOW}
                          />
                        )}
                        {/* Lead time — always visible, optional (sends 0 when empty) */}
                        <NumberInput
                          name="lead-time"
                          value={leadTime}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || (!isNaN(Number(val)) && Number(val) >= 0)) {
                              setLeadTime(val);
                            }
                          }}
                          placeholder={translate("single-product-offering.preparation-lead-time")}
                          classNames="flex-1 min-w-0"
                        />
                      </Flex>
                      {fieldErrors.availableFromDate && availabilityType !== AvailabilityTypeEnum.ALWAYS_IN_STOCK && (
                        <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                          {getRequiredFieldError(translate("single-product-offering.earliest-availability-date"))}
                        </Text>
                      )}
                    </Flex>
                  </Flex>

                  <HorizontalLine />

                  {/* UoM Conversion Ratio */}
                  {(selectedProduct.units_per_pallet > 0 || selectedProduct.units_per_container > 0) && (
                    <Flex className="flex-col gap-3 border-gray-200">
                      <Text fontWeight="semibold" color="lightGray">
                        {translate("single-product-offering.uom-conversion-ratio")}
                      </Text>
                      <Flex className="items-end gap-4">
                        {/* Container = Pieces */}
                        {selectedProduct.units_per_container > 0 && (
                          <Flex className="items-end gap-3 flex-1">
                            <Flex className="flex-col gap-2 flex-1">
                              <Text variant="span" fontWeight="medium" color="lightGray">Containers</Text>
                              <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                <Icon name={IconName.Container} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                <Text variant="div" className="text-gray-400">1</Text>
                              </div>
                            </Flex>
                            <Text variant="div" className="text-gray-600 mb-2">=</Text>
                            <Flex className="flex-col gap-2 flex-1">
                              <Text variant="span" fontWeight="medium" color="lightGray">Pieces</Text>
                              <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                <Icon name={IconName.Pieces} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                <Text variant="div" className="text-gray-400">{selectedProduct.units_per_container}</Text>
                              </div>
                            </Flex>
                          </Flex>
                        )}

                        {/* Vertical Divider */}
                        {selectedProduct.units_per_container > 0 && selectedProduct.units_per_pallet > 0 && (
                          <VerticalLine className="h-12 md:h-16 bg-gray-200" />
                        )}

                        {/* Pallets = Pieces */}
                        {selectedProduct.units_per_pallet > 0 && (
                          <Flex className="items-end gap-3 flex-1">
                            <Flex className="flex-col gap-2 flex-1">
                              <Text variant="span" fontWeight="medium" color="lightGray">Pallets</Text>
                              <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                <Icon name={IconName.Pallets} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                <Text variant="div" className="text-gray-400">1</Text>
                              </div>
                            </Flex>
                            <Text variant="div" className="text-gray-600 mb-2">=</Text>
                            <Flex className="flex-col gap-2 flex-1">
                              <Text variant="span" fontWeight="medium" color="lightGray">Pieces</Text>
                              <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                <Icon name={IconName.Pieces} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                <Text variant="div" className="text-gray-400">{selectedProduct.units_per_pallet}</Text>
                              </div>
                            </Flex>
                          </Flex>
                        )}

                        {/* Vertical Divider */}
                        {selectedProduct.units_per_container > 0 && selectedProduct.units_per_pallet > 0 && (
                          <VerticalLine className="h-12 md:h-16 bg-gray-200" />
                        )}

                        {/* Container = Pallets */}
                        {selectedProduct.units_per_container > 0 && selectedProduct.units_per_pallet > 0 && (
                          <Flex className="items-end gap-3 flex-1">
                            <Flex className="flex-col gap-2 flex-1">
                              <Text variant="span" fontWeight="medium" color="lightGray">Containers</Text>
                              <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                <Icon name={IconName.Container} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                <Text variant="div" className="text-gray-400">1</Text>
                              </div>
                            </Flex>
                            <Text variant="div" className="text-gray-600 mb-2">=</Text>
                            <Flex className="flex-col gap-2 flex-1">
                              <Text variant="span" fontWeight="medium" color="lightGray">Pallets</Text>
                              <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                <Icon name={IconName.Pallets} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                <Text variant="div" className="text-gray-400">
                                  {Math.round((selectedProduct.units_per_container / selectedProduct.units_per_pallet) * 100) / 100}
                                </Text>
                              </div>
                            </Flex>
                          </Flex>
                        )}
                      </Flex>
                    </Flex>
                  )}

                  <HorizontalLine />

                  {/* MOQ */}
                  <Flex className="flex-col gap-4">
                    <Text fontWeight="semibold" color="lightGray">{translate("pq-ingestion.moq")} *</Text>
                    <Flex className="flex-col gap-3">
                      <Flex className="items-center gap-4">
                        <label
                          className="flex items-center gap-2 cursor-pointer"
                          onClick={() => {
                            setMoqUnit(UnitTypeEnum.pieces);
                            setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false }));
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setMoqUnit(UnitTypeEnum.pieces);
                              setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false }));
                            }}
                            className="flex items-center justify-center"
                          >
                            <Icon
                              name={moqUnit === UnitTypeEnum.pieces ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked}
                              size={spacing[5]}
                              color={moqUnit === UnitTypeEnum.pieces ? colors.red[600] : colors.gray[400]}
                              className="cursor-pointer"
                            />
                          </button>
                          <Icon name={IconName.Pieces} size={spacing[5]} customStrokeWidth={2} color={colors.gray[900]} />
                          <Text className="text-sm text-gray-900">{translate("single-product-offering.pieces")}</Text>
                        </label>
                        <label
                          className="flex items-center gap-2 cursor-pointer"
                          onClick={() => {
                            setMoqUnit(UnitTypeEnum.pallets);
                            setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false }));
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setMoqUnit(UnitTypeEnum.pallets);
                              setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false }));
                            }}
                            className="flex items-center justify-center"
                          >
                            <Icon
                              name={moqUnit === UnitTypeEnum.pallets ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked}
                              size={spacing[5]}
                              color={moqUnit === UnitTypeEnum.pallets ? colors.red[600] : colors.gray[400]}
                              className="cursor-pointer"
                            />
                          </button>
                          <Icon name={IconName.Pallets} size={spacing[5]} customStrokeWidth={2} color={colors.gray[900]} />
                          <Text className="text-sm text-gray-900">{translate("single-product-offering.full-pallets-only")}</Text>
                        </label>
                        <label
                          className="flex items-center gap-2 cursor-pointer"
                          onClick={() => {
                            setMoqUnit(UnitTypeEnum.containers);
                            setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false }));
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setMoqUnit(UnitTypeEnum.containers);
                              setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false }));
                            }}
                            className="flex items-center justify-center"
                          >
                            <Icon
                              name={moqUnit === UnitTypeEnum.containers ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked}
                              size={spacing[5]}
                              color={moqUnit === UnitTypeEnum.containers ? colors.red[600] : colors.gray[400]}
                              className="cursor-pointer"
                            />
                          </button>
                          <Icon name={IconName.Container} size={spacing[5]} customStrokeWidth={2} color={colors.gray[900]} />
                          <Text className="text-sm text-gray-900">{translate("single-product-offering.full-containers-only")}</Text>
                        </label>
                      </Flex>
                      <Flex className="gap-2">
                        <Flex className="flex-col gap-2">
                          <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.min-order-quantity")} *</Text>
                          <NumberInput
                            name="moq"
                            value={moq}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "" || (!isNaN(Number(val)) && Number(val) >= 0)) {
                                setMoq(val);
                                setFieldErrors((prev) => ({ ...prev, moq: false, moqExceedsQuantity: false }));
                              }
                            }}
                            placeholder="Quantity"
                            classNames={twMerge(
                              moqUnit === UnitTypeEnum.pieces ? "w-full" : "flex-1",
                              (fieldErrors.moq || fieldErrors.moqExceedsQuantity) && "border-red-500"
                            )}
                          />
                          {fieldErrors.moq && (
                            <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                              {getRequiredFieldError(translate("single-product-offering.min-order-quantity"))}
                            </Text>
                          )}
                        </Flex>
                        <Flex className="flex-col gap-2">
                          <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.pieces")}</Text>
                          <TextInput
                            name="moq-pieces"
                            value={
                              moq && !isNaN(Number(moq)) && Number(moq) > 0
                                ? moqUnit === UnitTypeEnum.pieces
                                  ? moq
                                  : moqUnit === UnitTypeEnum.pallets && selectedProduct?.units_per_pallet
                                    ? (Number(moq) * selectedProduct.units_per_pallet).toString()
                                    : moqUnit === UnitTypeEnum.containers && selectedProduct?.units_per_container
                                      ? (Number(moq) * selectedProduct.units_per_container).toString()
                                      : ""
                                : ""
                            }
                            isDisabled={true}
                            classNames={twMerge("w-24", fieldErrors.moqExceedsQuantity && "border-red-500")}
                          />
                          {fieldErrors.moqExceedsQuantity && (
                            <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                              {translate("single-product-offering.error.moq-exceeds-quantity")}
                            </Text>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>

                  <HorizontalLine />

                  {/* Pricing */}
                  <Flex className="flex-col gap-4">
                    <Text fontWeight="semibold" color="lightGray">{translate("single-product-offering.pricing")} *</Text>
                    <Flex className="items-center gap-6">
                      <Flex className="flex-col gap-3">
                        <Flex className="items-center gap-4">
                          <label
                            className="flex items-center gap-2 cursor-pointer"
                            onClick={() => setPriceUnit(PriceUnitEnum.pieces)}
                          >
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setPriceUnit(PriceUnitEnum.pieces);
                              }}
                              className="flex items-center justify-center"
                            >
                              <Icon
                                name={priceUnit === PriceUnitEnum.pieces ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked}
                                size={spacing[5]}
                                color={priceUnit === PriceUnitEnum.pieces ? colors.red[600] : colors.gray[400]}
                                className="cursor-pointer"
                              />
                            </button>
                            <Text className="text-sm text-gray-700">{translate("single-product-offering.price-per-pieces")}</Text>
                          </label>
                          {selectedProduct?.category === ProductCategory.Modules && (
                            <label
                              className="flex items-center gap-2 cursor-pointer"
                              onClick={() => setPriceUnit(PriceUnitEnum.wattpeak)}
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setPriceUnit(PriceUnitEnum.wattpeak);
                                }}
                                className="flex items-center justify-center"
                              >
                                <Icon
                                  name={priceUnit === PriceUnitEnum.wattpeak ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked}
                                  size={spacing[5]}
                                  color={priceUnit === PriceUnitEnum.wattpeak ? colors.red[600] : colors.gray[400]}
                                  className="cursor-pointer"
                                />
                              </button>
                              <Text className="text-sm text-gray-700">{translate("single-product-offering.price-per-wattpeak")}</Text>
                            </label>
                          )}
                        </Flex>
                        <Flex className="items-center gap-2">
                          <NumberInput
                            name="price"
                            value={price}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "" || (!isNaN(Number(val)) && Number(val) >= 0)) {
                                setPrice(val);
                                setFieldErrors((prev) => ({ ...prev, price: false }));
                              }
                            }}
                            placeholder="Price"
                            classNames={twMerge("flex-1", fieldErrors.price && "border-red-500")}
                          />
                          <TextInput
                            name="currency"
                            value={translate("single-product-offering.currency")}
                            isDisabled={true}
                            classNames="w-24"
                          />
                        </Flex>
                        {fieldErrors.price && (
                          <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                            {getRequiredFieldError(translate("single-product-offering.pricing"))}
                          </Text>
                        )}
                      </Flex>
                      <VerticalLine className="h-12 md:h-16 bg-gray-200" />
                      <Flex className="flex-col gap-3">
                        <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.offer-valid-until")} *</Text>
                        <DatePickerButton
                          value={validUntilDate || undefined}
                          onChange={(date) => {
                            setValidUntilDate(date);
                            setFieldErrors((prev) => ({ ...prev, validUntilDate: false }));
                          }}
                          placeholder="dd.mm.yyyy"
                          classNames={twMerge("w-full", fieldErrors.validUntilDate && "border-red-500")}
                          uniqueQueryId="valid-until-date"
                        />
                        {fieldErrors.validUntilDate && (
                          <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                            {getRequiredFieldError(translate("single-product-offering.offer-valid-until"))}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  </Flex>
                </CollapsibleSection>
              </Flex>

              {/* Handover */}
              <Flex className="flex-col gap-6 border border-gray-200 rounded-lg p-4 mb-4">
                <CollapsibleSection
                  title={translate("single-product-offering.handover")}
                  defaultExpanded={true}
                  contentClassName="gap-6"
                >
                  <Flex className="flex-col gap-2 pt-4">
                    <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.handover-location")} *</Text>
                    <IncotermLocationSelect
                      name="handover-location"
                      trackingContext="single_offer_create"
                      value={incotermLocation}
                      onChange={(location) => {
                        setIncotermLocation(location);
                        setFieldErrors((prev) => ({ ...prev, incotermLocation: false }));
                      }}
                      error={fieldErrors.incotermLocation}
                    />
                    {fieldErrors.incotermLocation && (
                      <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                        {getRequiredFieldError(translate("single-product-offering.handover-location"))}
                      </Text>
                    )}
                  </Flex>
                  <HorizontalLine />
                  <Flex className="flex-col gap-4">
                    <Text fontWeight="semibold" color="lightGray">{translate("single-product-offering.available-pickup-options")}</Text>
                    <Flex className="items-center gap-4">
                      <Flex className="flex-col gap-2">
                        <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.select-incoterm")} *</Text>
                        <Flex className={twMerge("items-center gap-3 flex-wrap rounded-md", fieldErrors.incoterm && "border border-red-500 p-2")}>
                          {INCOTERM_OPTIONS.map((option) => (
                            <label
                              key={option.value}
                              className="flex items-center gap-2 cursor-pointer"
                              onClick={() => setIncoterm(option.value)}
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIncoterm(option.value);
                                  setFieldErrors((prev) => ({ ...prev, incoterm: false }));
                                }}
                                className="flex items-center justify-center"
                              >
                                <Icon
                                  name={incoterm === option.value ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked}
                                  size={spacing[5]}
                                  color={incoterm === option.value ? colors.red[600] : colors.gray[400]}
                                  className="cursor-pointer"
                                />
                              </button>
                              <Text className="text-[10px] md:text-xs text-gray-900">{option.label}</Text>
                            </label>
                          ))}
                        </Flex>
                        {fieldErrors.incoterm && (
                          <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                            {getRequiredFieldError(translate("single-product-offering.select-incoterm"))}
                          </Text>
                        )}
                      </Flex>
                      <VerticalLine className="h-12 md:h-16 bg-gray-200" />
                      <Flex className="flex-col gap-2">
                        <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.included-logistic-cost")}</Text>
                        <Flex className="items-center gap-3">
                          <Switch
                            checked={includedLogisticCost}
                            onChange={(e) => {
                              e.preventDefault();
                              setIncludedLogisticCost(!includedLogisticCost);
                            }}
                          />
                          <Text className="text-sm text-gray-600">
                            {includedLogisticCost ? translate("single-product-offering.yes") : translate("single-product-offering.no")}
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>

                </CollapsibleSection>
              </Flex>
            </Flex>
          )}
        </Flex>

        {/* Action Buttons */}
        {selectedProduct && (
          <Flex className="gap-3 justify-between w-full pt-4">
            <Button
              buttonStyle={ButtonTypesEnum.default}
              text={translate("general.cancel")}
              onClick={handleClose}
              buttonClassName="px-6 py-2 border border-gray-300 w-fit"
              disabled={isSubmitting}
            />
            <Flex className="items-center gap-2">
              <Button
                buttonStyle={ButtonTypesEnum.primary}
                text={translate("single-product-offering.create-offer")}
                onClick={handleSubmit}
                disabled={isSubmitting}
                isLoading={isSubmitting}
                triggerShake={createOfferShakeTrigger}
                buttonClassName="px-6 py-2 w-fit"
              />
            </Flex>
          </Flex>
        )}
        {isSubmitting && (
          <div className="absolute inset-0 z-70 flex items-center justify-center pointer-events-none">
            <AndercoreLogoLoadingGif height={84} width={84} className="h-full bg-transparent" />
          </div>
        )}
      </div>

      {/* Product Search Dropdown - Rendered outside modal container using portal */}
      {typeof window !== "undefined" &&
        showProductDropdown &&
        productDropdownPosition &&
        createPortal(
          <div
            data-product-dropdown
            className="fixed z-9999"
            style={{
              top: `${productDropdownPosition.top}px`,
              left: `${productDropdownPosition.left}px`,
            }}
          >
            <div className="min-w-[400px] max-w-[500px] bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {productSearchResults.length > 0
                ? productSearchResults.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleProductSelect(product, e);
                    }}
                    onMouseDown={(e) => {
                      // Prevent the modal's outside click handler from firing
                      e.stopPropagation();
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <Text className="text-sm font-medium text-gray-900 whitespace-normal">{product.name}</Text>
                    <Flex className="items-center gap-2 mt-1">
                      {product.brand && (
                        <>
                          <Text className="text-xs text-gray-500">{product.brand}</Text>
                          {product.model && (
                            <>
                              <Text className="text-xs text-gray-400">•</Text>
                              <Text className="text-xs text-gray-500">{product.model}</Text>
                            </>
                          )}
                        </>
                      )}
                    </Flex>
                  </button>
                ))
                : (
                  <div className="px-4 py-5 text-left">
                    <Text className="text-sm text-gray-600 whitespace-normal">
                      {translate("single-product-offering.no-results-support")}
                    </Text>
                  </div>
                )}
            </div>
          </div>,
          document.body
        )}
    </Modal>
  );
};

export default CreateSingleOfferModal;
